import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Button,
  Input,
  Badge,
  Modal,
  Card,
  toast,
  Toaster,
  Spinner,
  Empty,
} from '../components/ui/index.js'
import { staff, billing, type PlanDto, type Entitlements } from '../lib/api.js'
import { PlatformAccessCard } from '../components/impersonation/PlatformAccessCard.js'
import { LeadModal } from '../components/LeadModal.js'
import type { PlatformUser } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { intlLocaleFor } from '@self-restaurant/shared'
import { getUserRoles } from '../lib/auth.js'

const ROLES = [
  { value: 'restaurant_owner', key: 'admin.users.role_owner' },
  { value: 'manager', key: 'admin.users.role_manager' },
  { value: 'kitchen', key: 'admin.users.role_kitchen' },
  { value: 'waiter', key: 'admin.users.role_waiter' },
]

const ROLE_LABEL_KEYS: Record<string, string> = {
  restaurant_owner: 'admin.users.role_owner',
  manager: 'admin.users.role_manager',
  kitchen: 'admin.users.role_kitchen',
  waiter: 'admin.users.role_waiter',
  customer: 'admin.users.role_customer',
}

/** Roles que um Gerente (#284) não pode atribuir nem gerenciar. */
const OWNER_ROLES = ['restaurant_owner', 'platform_owner']

export function RestaurantUsers() {
  const { t, lang } = useI18n()
  // Gerente: sem plano/assinatura e sem mexer em Dono (#284)
  const isManager = (() => {
    const r = getUserRoles()
    return r.includes('manager') && !r.some((x) => OWNER_ROLES.includes(x))
  })()
  const defaultRoles = isManager ? ['waiter'] : ['restaurant_owner']
  const roleOptions = isManager ? ROLES.filter((r) => !OWNER_ROLES.includes(r.value)) : ROLES
  const { slug } = useParams<{ slug: string }>()

  const [tenant, setTenant] = useState<{ id: string; slug: string; name: string } | null>(null)
  const [users, setUsers] = useState<PlatformUser[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const [email, setEmail] = useState('')
  const [roles, setRoles] = useState<string[]>(defaultRoles)
  const [displayName, setDisplayName] = useState('')

  const [seats, setSeats] = useState<Record<string, { used: number; limit: number | null }>>({})
  const [editUser, setEditUser] = useState<PlatformUser | null>(null)
  const [editRoles, setEditRoles] = useState<string[]>([])
  const [plan, setPlan] = useState<PlanDto | null>(null)
  const [planBusy, setPlanBusy] = useState(false)
  const [ent, setEnt] = useState<Entitlements | null>(null)
  const [hasStripe, setHasStripe] = useState(false)
  const [leadPlan, setLeadPlan] = useState<'small_business' | 'pro' | null>(null)

  useEffect(() => {
    if (isManager) return // /billing é só do Dono
    billing
      .subscription()
      .then((data) => {
        setPlan(data.plan)
        setEnt(data.entitlements ?? null)
        setHasStripe(!!data.subscription.stripe_customer_id)
      })
      .catch(() => setPlan(null))
  }, [slug, isManager])

  async function startCheckout(planCode: string) {
    setPlanBusy(true)
    try {
      const { url } = await billing.checkout(planCode)
      window.location.href = url
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.plan.checkout_err'))
    } finally {
      setPlanBusy(false)
    }
  }

  async function openPortal() {
    setPlanBusy(true)
    try {
      const { url } = await billing.portal()
      window.location.href = url
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.plan.portal_err'))
    } finally {
      setPlanBusy(false)
    }
  }

  function toggleRole(role: string) {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]))
  }

  useEffect(() => {
    if (slug) load()
  }, [slug])

  async function load() {
    setLoading(true)
    try {
      // rotas /admin/users: tenant do request (dono e platform_owner)
      const data = await staff.list()
      setTenant(data.tenant)
      setUsers(data.users)
      setSeats(data.seats ?? {})
    } catch {
      toast.error(t('admin.users.load_err'))
    } finally {
      setLoading(false)
    }
  }

  function openModal() {
    setEmail('')
    setRoles(defaultRoles)
    setDisplayName('')
    setModalOpen(true)
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    if (!tenant) return
    setSaving(true)
    try {
      const payload: Parameters<typeof staff.invite>[0] = { email, roles }
      if (displayName) payload.display_name = displayName
      await staff.invite(payload)
      toast.success(t('admin.users.invited_toast'))
      setModalOpen(false)
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.users.invite_err'))
    } finally {
      setSaving(false)
    }
  }

  /** Gerente não edita quem é Dono (#284). */
  function canManage(u: PlatformUser): boolean {
    return !(isManager && (u.roles ?? [u.role]).some((r) => OWNER_ROLES.includes(r)))
  }

  function openEditRoles(u: PlatformUser) {
    setEditUser(u)
    setEditRoles((u.roles ?? [u.role]).filter((r) => r !== 'customer'))
  }

  async function saveRoles() {
    if (!editUser) return
    setSaving(true)
    try {
      await staff.setRoles(editUser.id, editRoles)
      toast.success(t('admin.users.roles_saved_toast'))
      setEditUser(null)
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('admin.users.roles_err'))
    } finally {
      setSaving(false)
    }
  }

  async function setActive(user: PlatformUser, isActive: boolean) {
    if (!tenant) return
    const key = isActive ? 'admin.users.reactivate_confirm' : 'admin.users.deactivate_confirm'
    if (!confirm(t(key, { email: user.email ?? '' }))) return
    try {
      await staff.setActive(user.id, isActive)
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, is_active: isActive } : u)))
      toast.success(
        isActive ? t('admin.users.reactivated_toast') : t('admin.users.deactivated_toast'),
      )
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : isActive
            ? t('admin.users.reactivate_err')
            : t('admin.users.deactivate_err'),
      )
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Toaster />
      <LeadModal
        plan={leadPlan}
        country={ent?.country_code ?? 'PT'}
        onClose={() => setLeadPlan(null)}
      />

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {tenant ? tenant.name : t('admin.users.title')}
          </h1>
          {tenant && <p className="text-sm text-gray-500 mt-0.5 font-mono">{tenant.slug}</p>}
        </div>
        <Button onClick={openModal}>{t('admin.users.invite_new')}</Button>
      </div>

      {Object.keys(seats).length > 0 && (
        <Card>
          <p className="text-sm font-bold text-gray-900 mb-2">{t('admin.users.seats')}</p>
          <div className="flex flex-wrap gap-2" data-testid="staff-seats">
            {(['manager', 'waiter', 'kitchen'] as const).map((r) => {
              const s = seats[r]
              if (!s) return null
              const full = s.limit !== null && s.used >= s.limit
              return (
                <Badge key={r} color={full ? 'red' : 'gray'}>
                  <span data-testid={`seats-${r}`}>
                    {t(ROLE_LABEL_KEYS[r]!)}: {s.used}/{s.limit ?? '∞'}
                  </span>
                </Badge>
              )
            })}
          </div>
        </Card>
      )}

      {plan && (
        <Card>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <p className="text-sm font-bold text-gray-900">
                {t('admin.plan.current')}: {plan.name}
                {plan.price_cents > 0 && plan.public_price !== false && (
                  <span className="ml-2 text-gray-500 font-normal">
                    {new Intl.NumberFormat(intlLocaleFor(lang, ent?.country_code), {
                      style: 'currency',
                      currency: plan.currency,
                    }).format(plan.price_cents / 100)}
                    /{t('admin.plan.per_month')}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('admin.plan.kitchen')}: {plan.max_kitchen ?? t('admin.plans.unlimited')} ·{' '}
                {t('admin.plan.waiters')}: {plan.max_waiters ?? t('admin.plans.unlimited')} ·{' '}
                {t('admin.plan.managers')}: {plan.max_managers ?? t('admin.plans.unlimited')} ·{' '}
                {t('admin.plan.translations')}:{' '}
                {plan.translation_locales === null
                  ? t('admin.plans.all_languages')
                  : plan.translation_locales}
              </p>
              {ent && (
                <PlanStatus
                  ent={ent}
                  price={
                    plan.self_serve && plan.price_cents > 0 && plan.public_price !== false
                      ? { cents: plan.price_cents, currency: plan.currency }
                      : null
                  }
                />
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {/* #293: só o bundle tem checkout; Small/Pro = pedido de contato */}
              {plan.self_serve && ent?.access !== 'active' && ent?.access !== 'grace' && (
                <Button
                  size="sm"
                  disabled={planBusy}
                  onClick={() => startCheckout(plan.code)}
                  data-testid="plan-activate"
                >
                  {t('admin.plan.activate_bundle')}
                </Button>
              )}
              {hasStripe && (
                <Button size="sm" variant="secondary" disabled={planBusy} onClick={openPortal}>
                  {t('admin.plan.manage')}
                </Button>
              )}
              {ent?.homologated_full && plan.kind === 'bundle' && (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setLeadPlan('small_business')}
                    data-testid="plan-info-sb"
                  >
                    {t('admin.plan.info_sb')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setLeadPlan('pro')}
                    data-testid="plan-info-pro"
                  >
                    {t('admin.plan.info_pro')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner className="w-8 h-8 text-indigo-600" />
        </div>
      ) : users.length === 0 ? (
        <Empty
          title={t('admin.users.empty')}
          description={t('admin.users.empty_hint')}
          action={<Button onClick={openModal}>{t('admin.users.invite')}</Button>}
        />
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {t('admin.users.email')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {t('admin.users.name')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {t('admin.users.role')}
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">
                  {t('admin.users.status')}
                </th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 text-gray-900">{u.email}</td>
                  <td className="py-3 px-4 text-gray-500">{u.display_name ?? '—'}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles ?? [u.role]).map((r) => {
                        const key = ROLE_LABEL_KEYS[r]
                        return (
                          <Badge key={r} color={r === 'restaurant_owner' ? 'indigo' : 'gray'}>
                            {key ? t(key) : r}
                          </Badge>
                        )
                      })}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge color={u.is_active ? 'green' : 'red'}>
                      {u.is_active ? t('admin.users.active') : t('admin.users.inactive')}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    {canManage(u) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditRoles(u)}
                        data-testid={`edit-roles-${u.id}`}
                      >
                        {t('admin.users.edit_roles')}
                      </Button>
                    )}
                    {!canManage(u) ? null : u.is_active ? (
                      <Button size="sm" variant="ghost" onClick={() => setActive(u, false)}>
                        {t('admin.users.deactivate')}
                      </Button>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={() => setActive(u, true)}>
                        {t('admin.users.reactivate')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <PlatformAccessCard />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          tenant
            ? t('admin.users.invite_title_named', { name: tenant.name })
            : t('admin.users.invite_title')
        }
        footer={
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              {t('admin.common.cancel')}
            </Button>
            <Button onClick={handleInvite} disabled={saving || roles.length === 0}>
              {saving ? t('admin.users.inviting') : t('admin.users.invite')}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleInvite} className="space-y-4">
          <Input
            label={t('admin.users.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('admin.users.email_ph')}
            required
          />
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">{t('admin.users.roles_hint')}</p>
            <div className="flex flex-wrap gap-2">
              {roleOptions.map(({ value, key }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleRole(value)}
                  className={
                    roles.includes(value)
                      ? 'rounded-lg border border-indigo-500 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700'
                      : 'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50'
                  }
                >
                  {roles.includes(value) ? '✓ ' : ''}
                  {t(key)}
                </button>
              ))}
            </div>
            {roles.length === 0 && (
              <p className="text-xs text-red-600 mt-1">{t('admin.users.roles_required')}</p>
            )}
          </div>
          <Input
            label={t('admin.users.name_optional')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('admin.users.name_ph')}
          />
        </form>
      </Modal>

      <Modal
        open={editUser !== null}
        onClose={() => setEditUser(null)}
        title={t('admin.users.edit_roles_title', { email: editUser?.email ?? '' })}
        footer={
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setEditUser(null)}>
              {t('admin.common.cancel')}
            </Button>
            <Button onClick={saveRoles} disabled={saving || editRoles.length === 0}>
              {t('admin.common.save')}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 mb-2">{t('admin.users.roles_seat_hint')}</p>
        <div className="flex flex-wrap gap-2">
          {roleOptions.map(({ value, key }) => (
            <button
              key={value}
              type="button"
              data-testid={`role-toggle-${value}`}
              onClick={() =>
                setEditRoles((prev) =>
                  prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value],
                )
              }
              className={
                editRoles.includes(value)
                  ? 'rounded-lg border border-indigo-500 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700'
                  : 'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50'
              }
            >
              {editRoles.includes(value) ? '✓ ' : ''}
              {t(key)}
            </button>
          ))}
        </div>
        {editRoles.length === 0 && (
          <p className="text-xs text-red-600 mt-1">{t('admin.users.roles_required')}</p>
        )}
      </Modal>
    </div>
  )
}

/** Estado do acesso público (#293): trial, ativo, carência ou só prévia. */
function PlanStatus({
  ent,
  price,
}: {
  ent: Entitlements
  /** preço da assinatura self-serve (bundle) — nunca um valor fixo no texto */
  price: { cents: number; currency: string } | null
}) {
  const { t, lang } = useI18n()
  // #428: língua + país do tenant para o Intl (o preço segue a moeda do PLANO)
  const priceLocale = intlLocaleFor(lang, ent.country_code)
  const date = (ts: number | null) => (ts ? new Date(ts * 1000).toLocaleDateString() : '')
  if (ent.access === 'trial')
    return (
      <p className="text-xs text-indigo-700 mt-1" data-testid="plan-status">
        {t('admin.plan.status_trial', { date: date(ent.trial_end) })}
      </p>
    )
  if (ent.access === 'grace')
    return (
      <p className="text-xs text-amber-700 mt-1" data-testid="plan-status">
        {t('admin.plan.status_grace', { date: date(ent.grace_until) })}
      </p>
    )
  if (ent.access === 'inactive')
    return (
      <p className="text-xs text-red-700 mt-1" data-testid="plan-status">
        {price
          ? t('admin.plan.status_inactive', { price: formatPrice(price, priceLocale) })
          : t('admin.plan.status_inactive_noprice')}
      </p>
    )
  return null
}

/** "€5" / "5,00 €" conforme o idioma do painel; fallback: "5.00 EUR". */
function formatPrice(price: { cents: number; currency: string }, locale: string): string {
  const amount = price.cents / 100
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: price.currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${price.currency}`
  }
}
